// Fallback thumbnail images for ALL foods
// Uses local "whole" prep images so cards always show an image,
// even when image_url is null or Supabase Storage is unreachable.

// --- Original 26 foods ---
import appleWhole from "@/assets/foods/prep/apple-whole.webp";
import avocadoWhole from "@/assets/foods/prep/avocado-whole.webp";
import bananaWhole from "@/assets/foods/prep/banana-whole.webp";
import beefWhole from "@/assets/foods/prep/beef-whole.webp";
import bellPepperWhole from "@/assets/foods/prep/bell-pepper-whole.webp";
import broccoliWhole from "@/assets/foods/prep/broccoli-whole.webp";
import carrotWhole from "@/assets/foods/prep/carrot-whole.webp";
import cauliflowerWhole from "@/assets/foods/prep/cauliflower-whole.webp";
import chickenWhole from "@/assets/foods/prep/chicken-whole.webp";
import codWhole from "@/assets/foods/prep/cod-whole.webp";
import cucumberWhole from "@/assets/foods/prep/cucumber-whole.webp";
import eggWhole from "@/assets/foods/prep/egg-whole.webp";
import groundMeatWhole from "@/assets/foods/prep/ground-meat-whole.webp";
import mangoWhole from "@/assets/foods/prep/mango-whole.webp";
import orangeWhole from "@/assets/foods/prep/orange-whole.webp";
import pastaWhole from "@/assets/foods/prep/pasta-whole.webp";
import pearWhole from "@/assets/foods/prep/pear-whole.webp";
import porridgeWhole from "@/assets/foods/prep/porridge-whole.webp";
import potatoWhole from "@/assets/foods/prep/potato-whole.webp";
import quinoaWhole from "@/assets/foods/prep/quinoa-whole.webp";
import rutabagaWhole from "@/assets/foods/prep/rutabaga-whole.webp";
import salmonWhole from "@/assets/foods/prep/salmon-whole.webp";
import sweetPotatoWhole from "@/assets/foods/prep/sweet-potato-whole.webp";
import tomatoWhole from "@/assets/foods/prep/tomato-whole.webp";

// --- 12 new foods ---
import oatsWhole from "@/assets/foods/prep/oats-whole.webp";
import spinachWhole from "@/assets/foods/prep/spinach-whole.webp";
import zucchiniWhole from "@/assets/foods/prep/zucchini-whole.webp";
import peasWhole from "@/assets/foods/prep/peas-whole.webp";
import blueberryWhole from "@/assets/foods/prep/blueberry-whole.webp";
import plumWhole from "@/assets/foods/prep/plum-whole.webp";
import lentilsWhole from "@/assets/foods/prep/lentils-whole.webp";
import yogurtWhole from "@/assets/foods/prep/yogurt-whole.webp";
import cottageCheeseWhole from "@/assets/foods/prep/cottage-cheese-whole.webp";
import breadWhole from "@/assets/foods/prep/bread-whole.webp";
import peanutButterWhole from "@/assets/foods/prep/peanut-butter-whole.webp";
import watermelonWhole from "@/assets/foods/prep/watermelon-whole.webp";

/**
 * Complete mapping of normalized food names → local thumbnail images.
 * Keys are lowercase, with parenthetical suffixes stripped.
 */
const FOOD_THUMBNAILS: Record<string, string> = {
  // Original foods
  "agurk": cucumberWhole,
  "appelsin": orangeWhole,
  "avokado": avocadoWhole,
  "babygrøt": porridgeWhole,
  "banan": bananaWhole,
  "blomkål": cauliflowerWhole,
  "brokkoli": broccoliWhole,
  "egg": eggWhole,
  "eple": appleWhole,
  "gulrot": carrotWhole,
  "kålrot": rutabagaWhole,
  "kjøttdeig": groundMeatWhole,
  "kylling": chickenWhole,
  "laks": salmonWhole,
  "mango": mangoWhole,
  "paprika": bellPepperWhole,
  "pasta": pastaWhole,
  "potet": potatoWhole,
  "pære": pearWhole,
  "quinoa": quinoaWhole,
  "storfekjøtt": beefWhole,
  "søtpotet": sweetPotatoWhole,
  "tomat": tomatoWhole,
  "torsk": codWhole,

  // New foods
  "havregryn": oatsWhole,
  "spinat": spinachWhole,
  "squash": zucchiniWhole,
  "erter": peasWhole,
  "blåbær": blueberryWhole,
  "plomme": plumWhole,
  "linser": lentilsWhole,
  "yoghurt naturell": yogurtWhole,
  "cottage cheese": cottageCheeseWhole,
  "brød": breadWhole,
  "peanøttsmør": peanutButterWhole,
  "vannmelon": watermelonWhole,
};

/**
 * Returns a local thumbnail for a food by name, or undefined if none exists.
 * Normalizes: lowercase, trims, strips parenthetical suffixes and trailing spaces.
 * Examples: "Erter (grønne)" → "erter", "Gulrot " → "gulrot", "Laks (most)" → "laks"
 */
export function getFoodThumbnail(foodName: string): string | undefined {
  const normalized = foodName
    .toLowerCase()
    .replace(/\s*\(.*?\)\s*$/, '')
    .trim();
  return FOOD_THUMBNAILS[normalized];
}
